import styled, { css } from 'styled-components';
import { devices, breakpoints } from './landing-page-breakpoints/breakpoints';
import {
  flexBoxCenter,
  justifyContentSpaceBetween,
  justifyContentSpaceEvenly,
} from '../styles/commonStyles';
import { useState } from 'react';
import { useEffect } from 'react';
import { useMediaQuery } from 'react-responsive';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { addUserInfo, handleAccessToken } from '../store/slices/userSlice';
import { useMutation } from 'react-query';
import { loginService, resetPasswordService } from '../../services';
import { NOT_ALLOWED_ROLES, ROLES } from '../../constants';
import {
  describeEmptyField,
  describeLoginError,
} from '../../helpers/describeLoginError';

const LoginBox = ({ isEnglish }) => {
  const navigation = useNavigate();
  const [errorMessage, setErrorMessage] = useState('');
  // responsive design
  const isUserNotAllowed=(user)=>{
    return NOT_ALLOWED_ROLES.includes(user.user_role);
  }
  const isMobile = useMediaQuery({ query: '(max-width:600px)' });
  const emptyUsernameMessage = describeEmptyField('username', isEnglish);
  const emptyPasswordMessage = describeEmptyField('password', isEnglish);
  const forgotPasswordMessage = isEnglish
    ? 'forgot your password?'
    : 'Mot de passe oublié?';
  const resetPasswordInstruction = isEnglish
    ? 'enter your account name to reset password'
    : 'Entrez votre nom de compte pour réinitialiser le mot de passe.';
  const { mutate: mutateLogin } = useMutation(loginService, {
    onSuccess: (data) => {
      if (data?.token) {
        if(isUserNotAllowed(data.user)){
          alert('You are not allowed to login');
          return;
        }
        navigation('/');
        localStorage.setItem('access_token', data.token);
        localStorage.setItem("loginTime", new Date());
        dispatch(handleAccessToken(data.token));
        dispatch(addUserInfo(data.user));
      } else {
        // Some deployments answer rejected credentials with 201 and an empty
        // body, so the request resolves and onError never runs. No token is
        // the only signal that the login failed, and it carries no detail.
        setErrorMessage(describeLoginError({}, isEnglish));
      }
    },
    onError: (error) => {
      setErrorMessage(describeLoginError(error, isEnglish));
    },
  });
  const { mutate: mutateResetPassword } = useMutation(resetPasswordService);
  const dispatch = useDispatch();

  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [remember, setRemember] = useState(false);
  const [resetAccount, setResetAccount] = useState('');
  const [displayResetPasswordBox, setDisplayResetPasswordBox] = useState(false);

  useEffect(() => {
    const storedUsername = localStorage.getItem('rememberedUsername');

    if (storedUsername) {
      setUsername(storedUsername);
      setRemember(true);
    }
  }, []);

  const handleRememberChange = (event) => {
    setRemember(event.target.checked);
    if (event.target.checked) {
      localStorage.setItem('rememberedUsername', username);
    } else {
      localStorage.removeItem('rememberedUsername');
    }
  };

  const handleUserNameChange = (e) => {
    setUsername(e.target.value);
    setErrorMessage('');
  };

  const handlePasswordChange = (e) => {
    setPassword(e.target.value);
    setErrorMessage('');
  };

  useEffect(() => {
    if (remember) {
      localStorage.setItem('rememberedUsername', username);
    }
  }, [username, remember]);

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!username.trim()) {
      setErrorMessage(emptyUsernameMessage);
      return;
    }

    // The password is checked second so a blank form names the first field the
    // user has to fill in rather than the last one.
    if (!password) {
      setErrorMessage(emptyPasswordMessage);
      return;
    }

    mutateLogin({ username, password, remember });
  };

  const handleDisplayResetPasswordBox = () => {
    setErrorMessage('');
    setDisplayResetPasswordBox((isDisplayed) => !isDisplayed);
  };

  const handleResetPassword = (e) => {
    e.preventDefault();

    if (!resetAccount.trim()) {
      setErrorMessage(emptyUsernameMessage);
      return;
    }

    mutateResetPassword(resetAccount.trim());
  };

  return (
    <>
      {isEnglish ? (
        <Wrapper>
          <FlexWrapper>
            <Title>login</Title>
            {displayResetPasswordBox ? (
              <ResetPasswordForm
                id='forgot-password-form'
                onSubmit={handleResetPassword}
              >
                <ResetPasswordInstruction>
                  {resetPasswordInstruction}
                </ResetPasswordInstruction>
                <InputWrapper>
                  <Input
                    type='text'
                    placeholder='account name'
                    value={resetAccount}
                    onChange={(e) => {
                      setResetAccount(e.target.value);
                      setErrorMessage('');
                    }}
                  />
                </InputWrapper>
                {errorMessage && <ErrorMessage>{errorMessage}</ErrorMessage>}
                <Button type='submit'>
                  <P>send</P>
                </Button>
              </ResetPasswordForm>
            ) : (
              <Form onSubmit={handleSubmit}>
              <InputWrapper>
                <Input
                  type='text'
                  placeholder='account name'
                  value={username}
                  onChange={handleUserNameChange}
                ></Input>
              </InputWrapper>
              <InputWrapper>
                <Input
                  type='password'
                  placeholder='password'
                  value={password}
                  onChange={handlePasswordChange}
                ></Input>
              </InputWrapper>
              <InputWrapper isCheckBox={true}>
                <Input
                  isCheckBox={true}
                  type='checkbox'
                  checked={remember}
                  onChange={handleRememberChange}
                ></Input>
                <P>remember my account name</P>
              </InputWrapper>
              {errorMessage && <ErrorMessage>{errorMessage}</ErrorMessage>}
              <Button type='submit'>
                <P>enter</P>
              </Button>
              {/* <Button type='button'>
                <P>edit account</P>
              </Button> */}
              </Form>
            )}
            <A
              type='button'
              aria-expanded={displayResetPasswordBox}
              aria-controls='forgot-password-form'
              onClick={handleDisplayResetPasswordBox}
            >
              {displayResetPasswordBox ? 'back to login' : forgotPasswordMessage}
            </A>
          </FlexWrapper>
        </Wrapper>
      ) : (
        <Wrapper>
          <FlexWrapper>
            <Title>login</Title>
            {displayResetPasswordBox ? (
              <ResetPasswordForm
                id='forgot-password-form'
                onSubmit={handleResetPassword}
              >
                <ResetPasswordInstruction>
                  {resetPasswordInstruction}
                </ResetPasswordInstruction>
                <InputWrapper>
                  <Input
                    type='text'
                    placeholder='nom de compte'
                    value={resetAccount}
                    onChange={(e) => {
                      setResetAccount(e.target.value);
                      setErrorMessage('');
                    }}
                  />
                </InputWrapper>
                {errorMessage && <ErrorMessage>{errorMessage}</ErrorMessage>}
                <Button type='submit'>
                  <P>envoyer</P>
                </Button>
              </ResetPasswordForm>
            ) : (
              <Form onSubmit={handleSubmit}>
              <InputWrapper>
                <Input
                  type='text'
                  placeholder='account name'
                  value={username}
                  onChange={handleUserNameChange}
                ></Input>
              </InputWrapper>
              <InputWrapper>
                <Input
                  type='password'
                  placeholder='password'
                  value={password}
                  onChange={handlePasswordChange}
                ></Input>
              </InputWrapper>
              <InputWrapper isCheckBox={true}>
                <Input
                  isCheckBox={true}
                  type='checkbox'
                  checked={remember}
                  onChange={handleRememberChange}
                ></Input>
                <P>Mémoriser mon mot de passe</P>
              </InputWrapper>
              {errorMessage && <ErrorMessage>{errorMessage}</ErrorMessage>}
              <Button type='submit'>
                <P>entrer</P>
              </Button>
              <Button type='button'>
                <P>Modifier le compte</P>
              </Button>
              </Form>
            )}
            <A
              type='button'
              aria-expanded={displayResetPasswordBox}
              aria-controls='forgot-password-form'
              onClick={handleDisplayResetPasswordBox}
            >
              {displayResetPasswordBox
                ? 'retour à la connexion'
                : forgotPasswordMessage}
            </A>
          </FlexWrapper>
        </Wrapper>
      )}
    </>
  );
};

export default LoginBox;

// {isMobile ? (
//   <Wrapper>
//     <FlexWrapper>
//       <Title>login</Title>
//       <Form onSubmit={handleSubmit}>
//         <InputWrapper>
//           <Input
//             type='text'
//             placeholder='account name'
//             value={username}
//             onChange={handleUserNameChange}
//           ></Input>
//         </InputWrapper>
//         <InputWrapper>
//           <Input
//             type='password'
//             placeholder='password'
//             value={password}
//             onChange={handlePasswordChange}
//           ></Input>
//         </InputWrapper>
//         <InputWrapper isCheckBox={true}>
//           <Input
//             isCheckBox={true}
//             type='checkbox'
//             checked={remember}
//             onChange={handleRememberChange}
//           ></Input>
//           <P>remember my account name</P>
//         </InputWrapper>
//         <Button type='submit'>
//           <P>enter</P>
//         </Button>
//         <Button>
//           <P>edit account</P>
//         </Button>
//       </Form>
//       <P>forgot your password?</P>
//     </FlexWrapper>
//   </Wrapper>
// ) : (

const Wrapper = styled.div`
  width: 100%;
  max-width: 432px;

  height: auto;
  min-height: 454px;

  background: transparent linear-gradient(180deg, #233a544d 0%, #060d19 100%);
  box-shadow: inset 0px 0px 2px #ffffff7a, 0px 0px 3px #000000;
  border: 0.5px solid #000000;
  border-radius: 12px;
  ${flexBoxCenter}

  @media only screen and ${devices.mm} {
    max-width: 412px;
  }
`;

const FlexWrapper = styled.div`
  width: 100%;
  height: auto;
  min-height: 380px;
  padding: 24px 0;
  box-sizing: border-box;
  ${justifyContentSpaceBetween}
  flex-direction: column;
`;

const Title = styled.p`
  font-size: 16px;
  letter-spacing: 1.6px;
`;

const Form = styled.form`
  width: 100%;
  max-width: 372px;
  height: auto;
  min-height: 318px;
  padding: 0 20px;
  box-sizing: border-box;
  ${justifyContentSpaceEvenly}
  flex-direction: column;
`;

const ResetPasswordForm = styled.form`
  width: 100%;
  max-width: 372px;
  min-height: 220px;
  padding: 20px;
  box-sizing: border-box;
  ${justifyContentSpaceEvenly}
  flex-direction: column;
`;

const ResetPasswordInstruction = styled.p`
  margin: 0;
  font-size: 12px;
  letter-spacing: 1.2px;
  text-align: center;
`;

const InputWrapper = styled.div`
  ${({ isCheckBox }) =>
    isCheckBox
      ? css`
          width: 100%;
          max-width: 278px;
          height: 16px;
          /* margin-left: 64px; */
          ${justifyContentSpaceBetween}
          border-bottom:none;
        `
      : css`
          width: 100%;
          height: 20px;
          border-bottom: 1px solid #b4b4b4;
        `}
`;

const Input = styled.input`
  ${({ isCheckBox }) =>
    isCheckBox
      ? css`
          width: 10px;
          height: 10px;
        `
      : css`
          width: 100%;
          height: 100%;
          background-color: transparent;
          font-size: 12px;
          text-align: left;
          color: #ffffff;
          &::placeholder {
            color: #b4b4b4;
            text-align: left;
          }
          /* Chrome paints autofilled fields with an opaque background that
             wins over background-color; the long transition defers it and
             text-fill-color keeps the value readable on the dark card. */
          &:-webkit-autofill,
          &:-webkit-autofill:hover,
          &:-webkit-autofill:focus,
          &:-webkit-autofill:active {
            -webkit-text-fill-color: #ffffff;
            caret-color: #ffffff;
            transition: background-color 600000s 0s, color 600000s 0s;
          }
        `}
`;

const Button = styled.button`
  width: 208px;
  height: 29px;

  background: transparent linear-gradient(180deg, #3fb6e0 0%, #1f67be 100%);
  box-shadow: inset 0px 0px 1px #ffffffed, 0px 0px 2px #000000;
  border: 0.5px solid #000000;
  border-radius: 6px;

  ${flexBoxCenter}
`;

const P = styled.p`
  font-size: 12px;
  letter-spacing: 1.2px;
`;

const ErrorMessage = styled.p`
  color: #ff5c5c;
  font-size: 12px;
  letter-spacing: 1.2px;
  margin: 0;
  text-align: center;
`;

const A = styled.button`
  cursor: pointer;
  min-height: 44px;
  padding: 8px;
  border: 0;
  background: transparent;
  color: inherit;
  font-size: 12px;
  letter-spacing: 1.2px;
`;
